import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, FileText, CheckCircle, DollarSign, Wallet, Scale, CalendarDays } from 'lucide-react';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const PERIODS = [
  { label: '30d', days: 30 }, { label: '60d', days: 60 },
  { label: '90d', days: 90 }, { label: '6m', days: 180 }, { label: '1a', days: 365 }
];

const AGENDA_PERIODS = [
  { key: 'dia', label: 'Dia' },
  { key: 'semana', label: 'Semana' },
  { key: 'quinze', label: '15 dias' },
  { key: 'mes', label: 'Mês' },
];

function FaturamentoAgendaCard({ data }) {
  const [periodo, setPeriodo] = useState('dia');

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-zinc-900 to-zinc-900 border border-emerald-500/20 rounded-xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
            <CalendarDays size={18} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-medium text-white text-sm leading-tight">Faturamento da Agenda</h3>
            <p className="text-xs text-zinc-500">Serviços realizados via agendamento</p>
          </div>
        </div>
        <div className="flex gap-1 bg-zinc-800/60 rounded-xl p-1">
          {AGENDA_PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriodo(p.key)}
              className={`relative px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                periodo === p.key ? 'text-white' : 'text-zinc-400 hover:text-white'
              }`}>
              {periodo === p.key && (
                <motion.div layoutId="agenda-periodo-pill" className="absolute inset-0 bg-emerald-500 rounded-lg"
                  transition={{ type: 'spring', duration: 0.4, bounce: 0.2 }} />
              )}
              <span className="relative">{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={periodo}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="font-heading font-bold text-4xl text-white">
          {brl(data[periodo])}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [financeiro, setFinanceiro] = useState(null);

  useEffect(() => {
    api.get('/proposals/').then(r => setProposals(r.data)).finally(() => setLoading(false));
    api.get('/financeiro/resumo').then(r => setFinanceiro(r.data)).catch(() => {});
  }, []);

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - period);

  const filtered = proposals.filter(p => new Date(p.created_at) >= cutoff);
  const total = filtered.reduce((s, p) => s + p.valor, 0);
  const approved = filtered.filter(p => ['aprovado', 'em_andamento', 'concluido', 'pago'].includes(p.status));
  const approvalRate = filtered.length ? Math.round((approved.length / filtered.length) * 100) : 0;

  // Build chart data by day (last 30 days) or week (> 30)
  const buildChartData = () => {
    const map = {};
    filtered.forEach(p => {
      const d = new Date(p.created_at);
      let key;
      if (period <= 30) {
        key = `${d.getDate()}/${d.getMonth() + 1}`;
      } else {
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        key = `${weekStart.getDate()}/${weekStart.getMonth() + 1}`;
      }
      if (!map[key]) map[key] = { date: key, valor: 0, count: 0 };
      map[key].valor += p.valor;
      map[key].count += 1;
    });
    return Object.values(map).slice(-15);
  };

  const chartData = buildChartData();

  const kpis = [
    { label: 'Orçamentos gerados', value: filtered.length, icon: FileText, color: 'text-blue-400' },
    { label: 'Valor total', value: brl(total), icon: DollarSign, color: 'text-orange-400' },
    { label: 'Aprovados', value: approved.length, icon: CheckCircle, color: 'text-green-400' },
    { label: 'Taxa de aprovação', value: `${approvalRate}%`, icon: TrendingUp, color: 'text-purple-400' },
  ];

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs">
        <div className="text-zinc-400 mb-1">{label}</div>
        <div className="text-orange-400 font-mono">{brl(payload[0]?.value)}</div>
        <div className="text-zinc-500">{payload[1]?.value || payload[0]?.payload?.count} orç.</div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-bold text-white">Dashboard</h1>
        <div className="flex gap-1.5 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          {PERIODS.map(p => (
            <button key={p.days} onClick={() => setPeriod(p.days)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                period === p.days ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Financeiro */}
      {financeiro && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Faturamento do Dia', value: financeiro.faturamento.dia },
              { label: 'Faturamento da Semana', value: financeiro.faturamento.semana },
              { label: 'Faturamento em 15 dias', value: financeiro.faturamento.quinze },
              { label: 'Faturamento do Mês', value: financeiro.faturamento.mes },
            ].map(f => (
              <div key={f.label} className="stat-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-zinc-500">{f.label}</span>
                  <Wallet size={16} className="text-orange-400" />
                </div>
                <div className="font-heading font-bold text-xl text-white">{brl(f.value)}</div>
              </div>
            ))}
          </div>

          <FaturamentoAgendaCard data={financeiro.faturamento_agenda} />

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Scale size={16} className="text-orange-400" />
              <h3 className="font-medium text-white">Ponto de Equilíbrio do Mês</h3>
            </div>
            {financeiro.despesas_mes === 0 ? (
              <div className="text-sm text-zinc-400">
                Cadastre suas despesas do mês para ver o ponto de equilíbrio.{' '}
                <button onClick={() => navigate('/financeiro/despesas')} className="text-orange-400 hover:underline">
                  Cadastrar despesas
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-zinc-500 mb-0.5">Faturamento bruto mensal até hoje</div>
                    <div className="font-mono text-white">{brl(financeiro.faturamento.mes)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 mb-0.5">Despesas mensais</div>
                    <div className="font-mono text-white">{brl(financeiro.despesas_mes)}</div>
                  </div>
                </div>
                <div className={`rounded-lg p-3 text-sm font-medium ${financeiro.resultado_mes >= 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {financeiro.resultado_mes >= 0
                    ? `Ponto de equilíbrio do mês atingido. Superávit de ${brl(financeiro.resultado_mes)}`
                    : `Para sua empresa chegar ao ponto de equilíbrio deste mês falta o valor de ${brl(Math.abs(financeiro.resultado_mes))}`}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-zinc-500">{k.label}</span>
                <Icon size={16} className={k.color} />
              </div>
              <div className={`font-heading font-bold text-xl ${k.color}`}>{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <h3 className="font-medium text-white mb-4">Faturamento no período</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={customTooltip} />
              <Bar dataKey="valor" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
          <h3 className="font-medium text-white">Por status</h3>
          {['nao_enviado', 'enviado', 'aprovado', 'em_andamento', 'concluido', 'pago'].map(s => {
            const count = proposals.filter(p => p.status === s).length;
            const pct = proposals.length ? Math.round((count / proposals.length) * 100) : 0;
            const colors = {
              nao_enviado: 'bg-zinc-600',
              enviado: 'bg-blue-500',
              aprovado: 'bg-green-500',
              em_andamento: 'bg-yellow-500',
              concluido: 'bg-purple-500',
              pago: 'bg-emerald-500'
            };
            const labels = {
              nao_enviado: 'Não enviado', enviado: 'Enviado',
              aprovado: 'Aprovado', em_andamento: 'Em andamento',
              concluido: 'Concluído', pago: 'Pago'
            };
            return (
              <div key={s}>
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>{labels[s]}</span>
                  <span>{count}</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className={`h-full ${colors[s]} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent proposals */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h3 className="font-medium text-white">Últimos orçamentos</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800">
            <tr className="text-xs text-zinc-500">
              <th className="text-left px-4 py-2">#</th>
              <th className="text-left px-4 py-2">Título</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Cliente</th>
              <th className="text-right px-4 py-2">Valor</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {proposals.slice(0, 8).map(p => (
              <tr key={p.id} className="table-row-hover">
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">#{p.num}</td>
                <td className="px-4 py-2.5 text-white">{p.titulo}</td>
                <td className="px-4 py-2.5 text-zinc-400 hidden sm:table-cell">{p.cliente_nome || '—'}</td>
                <td className="px-4 py-2.5 text-right font-mono text-orange-400">{brl(p.valor)}</td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                  <span className={`badge badge-${p.status}`}>{p.status?.replace('_', ' ')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {proposals.length === 0 && !loading && (
          <div className="text-center py-8 text-zinc-500 text-sm">Nenhum orçamento ainda</div>
        )}
      </div>
    </div>
  );
}
